import { useEffect, useMemo, useState } from 'react';
import { endOfDay, format, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { hu } from 'date-fns/locale';
import { PiClockCounterClockwiseDuotone } from 'react-icons/pi';

import { Tooltip } from '@material-ui/core';

import OptimizationPipelinePopup, {
    OptimizationPipelinePopupHeader,
    PopupDateRangeQuickSelector,
    PopupToolbar,
    PopupToolbarGroup,
    PopupToolbarScrollGroup,
    PopupToolbarChip,
    PopupExportButtons
} from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';
import OptimizationPipelinePopupTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopupTable';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getSoftStatusClass } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/colorUtils';
import { parseTsToDate } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';
import { buildExportFilename, exportPopupTable } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';

const INITIAL_DATE_RANGE = { from: '', to: '', quickRange: 'ALL' };
const INITIAL_SORT = { key: 'ts', direction: 'desc' };
const STATUS_BADGE_CLASS = 'px-8 py-5 rounded-lg font-semibold whitespace-nowrap';
const STATUS_CELL_CLASS = 'flex flex-col text-12 px-8 py-5 rounded-lg';

function safeDate(value) { return parseTsToDate(value); }

function formatInputDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) { return ''; }
    return format(date, 'yyyy-MM-dd');
}

function formatDisplayDate(value) {
    const date = safeDate(value);
    return date ? format(date, 'yyyy.MM.dd.', { locale: hu }) : '';
}

function getDateRangeSubtitle(dateRange) {
    const from = formatDisplayDate(dateRange?.from);
    const to = formatDisplayDate(dateRange?.to);

    if (from && to) { return 'Showing data from ' + from + ' to ' + to; }
    if (from) { return 'Showing data from ' + from; }
    if (to) { return 'Showing data until ' + to; }

    return null;
}

function getStatusClass(color, baseClassName) {
    return baseClassName + ' ' + getSoftStatusClass(color);
}

function collectMergerData(data) {
    return (Array.isArray(data) ? data : [])
        .map((entry) => {
            if (!entry?.meta) return null;

            const meta = entry.meta;

            let merger = meta.merger;

            if (!merger) {
                const mergerKey = Object.keys(meta).find((key) => key.startsWith('merger:'));
                if (!mergerKey) return null;
                merger = meta[mergerKey];
            }

            if (!merger || !merger.inputs) return null;

            const inputs = merger.inputs;

            let syntheticFlag = false;
            for (const value of Object.values(inputs)) {
                if (value?.synthetic_data === true) {
                    syntheticFlag = true;
                    break;
                }
            }

            return {
                ts: entry.ts,
                data: inputs,
                syntheticFlag: syntheticFlag
            };
        })
        .filter(Boolean);
}

function getAllMergerKeys(data) {
    const set = new Set();

    for (const entry of (Array.isArray(data) ? data : []))
        if (entry?.data)
            Object.keys(entry.data).forEach((key) => set.add(key));

    return Array.from(set);
}

function formatDate(value) {
    const date = safeDate(value);
    return date ? format(date, 'yyyy.MM.dd', { locale: hu }) : '-';
}

function formatSyntheticValue(value) {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'Missing';
}

function formatMergerValue(value) {
    if (!value) return 'Missing data';
    if (value?.synthetic_data === true) return 'Synthetic';
    if (value?.synthetic_data === false) return 'Measured';
    return 'Unknown';
}

function renderSyntheticBadge(value) {
    if (value === true) { return (<div className={getStatusClass('yellow', STATUS_BADGE_CLASS)}>Yes</div>); }
    if (value === false) { return (<div className={getStatusClass('green', STATUS_BADGE_CLASS)}>No</div>); }
    return (<div className={getStatusClass('red', STATUS_BADGE_CLASS)}>Missing</div>);
}

function renderMergerCell(row, key) {
    const value = row?.data?.[key];
    if (!value) { return (<div className={getStatusClass('red', STATUS_CELL_CLASS)}><span className='font-semibold'>Missing data</span></div>); }
    const synthetic = value?.synthetic_data;
    if (synthetic === true) { return (<div className={getStatusClass('yellow', STATUS_CELL_CLASS)}><span className='font-semibold'>Synthetic</span></div>); }
    if (synthetic === false) { return (<div className={getStatusClass('green', STATUS_CELL_CLASS)}><span className='font-semibold'>Measured</span></div>); }
    return (<div className={getStatusClass('gray', STATUS_CELL_CLASS)}><span className='font-semibold'>Unknown</span></div>);
}

export default function UnitHistoryPopup(props) {
    const { popup, closePopup, getOnDemandResource, ensureOnDemandResource, ts } = useOptimizationPipeline();

    const [dateRange, setDateRange] = useState(INITIAL_DATE_RANGE);
    const [visibleDynamicKeys, setVisibleDynamicKeys] = useState([]);
    const [syntheticFilter, setSyntheticFilter] = useState('all');
    const [tableSort, setTableSort] = useState(INITIAL_SORT);

    const mergedMeasurementResource = getOnDemandResource('mergedMeasurementDataList');
    const mergedMeasurementDataList = mergedMeasurementResource?.data;
    const mergedMeasurementDataListLoading = mergedMeasurementResource?.loading;
    const mergedMeasurementDataListError = mergedMeasurementResource?.error;

    useEffect(() => {
        if (!popup || popup.type !== 'UNIT_HISTORY') return;
        ensureOnDemandResource('mergedMeasurementDataList');
    }, [popup, ensureOnDemandResource]);

    useEffect(() => {
        if (!popup || popup.type !== 'UNIT_HISTORY') return;
        setTableSort(INITIAL_SORT);
        setSyntheticFilter('all');
    }, [popup?.type, popup?.requestToken]);

    const mergerData = useMemo(() => {
        return collectMergerData(mergedMeasurementDataList);
    }, [mergedMeasurementDataList]);

    const mergerKeys = useMemo(() => {
        return getAllMergerKeys(mergerData);
    }, [mergerData]);

    useEffect(() => {
        setVisibleDynamicKeys(mergerKeys);
    }, [mergerKeys]);

    const dateBounds = useMemo(() => {
        const dates = mergerData
            .map((row) => safeDate(row?.ts))
            .filter(Boolean)
            .sort((a, b) => a.getTime() - b.getTime());

        if (!dates.length) { return { minDate: null, maxDate: null }; }

        return {
            minDate: dates[0],
            maxDate: dates[dates.length - 1]
        };
    }, [mergerData]);

    useEffect(() => {
        if (!dateBounds.minDate || !dateBounds.maxDate) return;

        const nextFrom = formatInputDate(dateBounds.minDate);
        const nextTo = formatInputDate(dateBounds.maxDate);

        setDateRange((prev) => {
            if (prev.from === nextFrom && prev.to === nextTo && prev.quickRange === 'ALL') { return prev; }
            return { from: nextFrom, to: nextTo, quickRange: 'ALL' };
        });
    }, [dateBounds]);

    const disabledQuickOptions = useMemo(() => {
        const { minDate, maxDate } = dateBounds;

        if (!minDate || !maxDate) {
            return {
                ALL: true,
                '1Y': true,
                '1M': true,
                '1W': true,
                '1D': true
            };
        }

        const minTime = minDate.getTime();

        return {
            ALL: false,
            '1Y': subYears(maxDate, 1).getTime() < minTime,
            '1M': subMonths(maxDate, 1).getTime() < minTime,
            '1W': subWeeks(maxDate, 1).getTime() < minTime,
            '1D': subDays(maxDate, 1).getTime() < minTime
        };
    }, [dateBounds]);

    const processorInfoByKey = useMemo(() => {
        const map = {};

        for (const row of mergerData) {
            const data = row?.data || {};

            for (const [key, value] of Object.entries(data)) {
                if (map[key]) continue;

                const processor = value?.data_origin?.data_processor;
                const name = value?.data_origin?.data_processor_name;

                if (processor || name) {
                    map[key] = { proc: processor || '-', name: name || '-' };
                }
            }
        }

        return map;
    }, [mergerData]);

    const filteredRows = useMemo(() => {
        let rows = mergerData;

        const fromDate = safeDate(dateRange?.from);
        const toDate = safeDate(dateRange?.to);

        if (fromDate || toDate) {
            const fromTime = fromDate ? fromDate.getTime() : -Infinity;
            const toTime = toDate ? endOfDay(toDate).getTime() : Infinity;

            rows = rows.filter((row) => {
                const date = safeDate(row?.ts);
                if (!date) return false;

                const time = date.getTime();
                return time >= fromTime && time <= toTime;
            });
        }

        if (syntheticFilter === 'yes') {
            rows = rows.filter((row) => row.syntheticFlag === true);
        } else if (syntheticFilter === 'no') {
            rows = rows.filter((row) => row.syntheticFlag === false);
        }

        return rows;
    }, [mergerData, dateRange, syntheticFilter]);

    const tableConfig = useMemo(() => {
        const baseColumns = [
            {
                key: 'ts',
                label: 'Timestamp',
                exportLabel: 'Timestamp',
                sortable: true,
                defaultSortDirection: 'desc',
                width: 140,
                minWidth: 140,
                align: 'left',
                getSortValue: (row) => row?.ts,
                exportValue: (row) => formatDate(row?.ts),
                renderCell: ({ row }) => formatDate(row?.ts)
            },
            {
                key: 'syntheticFlag',
                label: 'Synthetic',
                exportLabel: 'Synthetic',
                sortable: true,
                width: 120,
                minWidth: 120,
                align: 'center',
                getSortValue: (row) => {
                    if (row?.syntheticFlag === true) { return 1; }
                    if (row?.syntheticFlag === false) { return 0; }
                    return -1;
                },
                exportValue: (row) => formatSyntheticValue(row?.syntheticFlag),
                renderCell: ({ row }) => renderSyntheticBadge(row?.syntheticFlag)
            }
        ];

        const dynamicColumns = mergerKeys
            .filter((key) => visibleDynamicKeys.includes(key))
            .map((key) => {
                const info = processorInfoByKey[key] || { proc: '-', name: '-' };

                return {
                    key: key,
                    exportLabel: key,
                    label: (
                        <div className='flex flex-col leading-tight overflow-hidden' style={{ maxWidth: 160 }}>
                            <Tooltip title={key} arrow><span className='font-semibold truncate'>{key}</span></Tooltip>
                            <Tooltip title={info.proc} arrow><span className='text-10 opacity-50 truncate'>{info.proc}</span></Tooltip>
                            <Tooltip title={info.name} arrow><span className='text-10 opacity-50 truncate'>{info.name}</span></Tooltip>
                        </div>
                    ),
                    sortable: true,
                    minWidth: 160,
                    headerAlign: 'left',
                    align: 'center',
                    getSortValue: (row) => {
                        const value = row?.data?.[key];
                        if (!value) { return 0; }
                        if (value?.synthetic_data === true) { return 2; }
                        if (value?.synthetic_data === false) { return 1; }
                        return 0;
                    },
                    exportValue: (row) => formatMergerValue(row?.data?.[key]),
                    renderCell: ({ row }) => renderMergerCell(row, key)
                };
            });

        return {
            columns: [...baseColumns, ...dynamicColumns],
            sort: INITIAL_SORT,
            states: {
                loadingTitle: 'Loading unit history...',
                errorTitle: 'Failed to load unit history',
                emptyTitle: 'No unit history data available',
                noResultsTitle: 'No matching unit history rows'
            }
        };
    }, [mergerKeys, visibleDynamicKeys, processorInfoByKey]);

    const exportDisabled = mergedMeasurementDataListLoading || !!mergedMeasurementDataListError || !filteredRows.length;

    const handleExport = (formatType) => {
        exportPopupTable({
            tableConfig,
            rows: filteredRows,
            sort: tableSort,
            filename: buildExportFilename('unit-history', ts),
            format: formatType
        });
    };

    return (
        <OptimizationPipelinePopup open={!!popup} onClose={closePopup} width={popup?.width || '1440px'} minWidth={popup?.minWidth || 480} maxWidth={popup?.maxWidth || 1440} className={popup?.className}>
            <OptimizationPipelinePopupHeader title='Unit History' subtitle={getDateRangeSubtitle(dateRange)} icon={PiClockCounterClockwiseDuotone} onClose={closePopup}>
                <div className='flex items-center gap-8'>
                    <PopupDateRangeQuickSelector value={dateRange} onChange={setDateRange} minDate={dateBounds.minDate} maxDate={dateBounds.maxDate} disabledQuickOptions={disabledQuickOptions} />
                    <PopupExportButtons disabled={exportDisabled} onExcel={() => handleExport('excel')} onCsv={() => handleExport('csv')} />
                </div>
            </OptimizationPipelinePopupHeader>

            {mergerData.length > 0 && (
                <PopupToolbar>
                    <PopupToolbarGroup label='Synthetic:'>
                        <PopupToolbarChip label='All' active={syntheticFilter === 'all'} onClick={() => { setSyntheticFilter('all'); }} />
                        <PopupToolbarChip label='Yes' active={syntheticFilter === 'yes'} onClick={() => { setSyntheticFilter('yes'); }} />
                        <PopupToolbarChip label='No' active={syntheticFilter === 'no'} onClick={() => { setSyntheticFilter('no'); }} />
                    </PopupToolbarGroup>

                    <PopupToolbarScrollGroup label='Columns:'>
                        {mergerKeys.map((key) => {
                            const active = visibleDynamicKeys.includes(key);
                            return (<PopupToolbarChip key={key} label={key} active={active} onClick={() => { setVisibleDynamicKeys((prev) => { if (active) { return prev.filter((value) => value !== key); } return [...prev, key]; }); }} />);
                        })}
                    </PopupToolbarScrollGroup>
                </PopupToolbar>
            )}

            <OptimizationPipelinePopupTable
                tableConfig={tableConfig}
                rows={filteredRows}
                loading={mergedMeasurementDataListLoading}
                error={mergedMeasurementDataListError}
                minHeight={360}
                sort={tableSort}
                onSortChange={setTableSort}
                rowKey={(row, index) => row?.ts || index}
            />
            
        </OptimizationPipelinePopup>
    );
}